import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../../services/api';  // Import the configured API service instead of axios
import axios from 'axios'; // Import axios
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import TourForm from './TourForm';
import TourList from './TourList';

const ToursPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();
  
  // Preload locations and categories data as soon as the Tours page loads
  useEffect(() => {
    const preloadFormData = async () => {
      try {
        console.log('Preloading locations and categories data for tour form...');
        
        // Preload locations
        queryClient.prefetchQuery('tourFormLocations', async () => {
          const response = await api.get('/api/v1/admin/locations', {
            headers: { 'Cache-Control': 'no-cache' },
            timeout: 5000
          });
          console.log(`Preloaded ${Array.isArray(response.data) ? response.data.length : 'unknown number of'} locations`);
          return response.data;
        });
        
        // Preload categories
        queryClient.prefetchQuery('tourFormCategories', async () => {
          const response = await api.get('/api/v1/admin/categories', {
            headers: { 'Cache-Control': 'no-cache' },
            timeout: 5000
          });
          console.log(`Preloaded ${Array.isArray(response.data) ? response.data.length : 'unknown number of'} categories`);
          return response.data;
        });
      } catch (error) {
        console.error('Error preloading form data:', error);
      }
    };
    
    // Run the preloading immediately when the Tours page loads
    preloadFormData();
    
    // Also set up an interval to refresh the data every 30 seconds
    const refreshInterval = setInterval(preloadFormData, 30000);
    
    // Clean up the interval when the component unmounts
    return () => clearInterval(refreshInterval);
  }, [queryClient]); // Only run this effect when the queryClient changes

  // Fetch tours data with React Query
  const {
    data: toursData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(
    ['tours', currentPage, itemsPerPage, sortField, sortDirection, searchQuery],
    async () => {
      try {
        console.log('Attempting to fetch tours from API...');
        
        // Use a more detailed approach to debug the API call
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        console.log('API Base URL:', apiUrl);
        
        // Log the full URL being requested for debugging
        const fullUrl = `${apiUrl}/api/v1/admin/tours?sort_by=${sortField}&sort_direction=${sortDirection}&page=${currentPage}&per_page=${itemsPerPage}${searchQuery ? `&search=${searchQuery}` : ''}`;
        console.log('Full request URL:', fullUrl);
        
        // Make the API call with detailed error handling
        const response = await api.get('/api/v1/admin/tours', {
          params: {
            sort_by: sortField,
            sort_direction: sortDirection,
            page: currentPage,
            per_page: itemsPerPage,
            search: searchQuery || undefined
          },
          timeout: 15000 // 15 seconds timeout
        });
        
        console.log('API response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching tours:', error);
        
        // Log detailed error information
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
          console.error('Error response headers:', error.response.headers);
        } else if (error.request) {
          // The request was made but no response was received
          console.error('Error request:', error.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error message:', error.message);
        }
        
        throw new Error(`Failed to fetch tours: ${error.message}`);
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  );

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleAddTour = () => {
    console.log('Add Tour button clicked');
    setEditingTour(null);
    setShowForm(true);
  };

  const handleEditTour = (tour) => {
    setEditingTour(tour);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTour(null);
  };

  const handleDeleteTour = async (tourId) => {
    if (window.confirm('Are you sure you want to delete this tour? This action cannot be undone.')) {
      try {
        await api.delete(`/api/v1/admin/tours/${tourId}`);
        toast.success('Tour deleted successfully');
        queryClient.invalidateQueries('tours');
      } catch (error) {
        console.error('Error deleting tour:', error);
        toast.error('Failed to delete tour. Please try again later.');
      }
    }
  };

  const handleToggleFeatured = async (tourId, currentStatus) => {
    try {
      const response = await api.post(
        `/api/v1/admin/tours/${tourId}/toggle-featured`, 
        {}, // Empty body as we're just toggling
      );
      
      console.log('Toggle featured response:', response.data);
      toast.success(`Tour ${currentStatus ? 'removed from' : 'added to'} featured successfully`);
      
      // Invalidate tours query to refresh the data
      queryClient.invalidateQueries('tours');
      
      // Also invalidate the featured tours query if it exists
      queryClient.invalidateQueries('featuredTours');
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast.error('Failed to update featured status. Please try again later.');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      console.log('Form submission started');
      
      // Log form data for debugging
      console.log('Form data entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }
      
      try {
        let response;
        
        if (editingTour) {
          // Update existing tour
          console.log('Updating tour with ID:', editingTour.id);
          response = await api.post(`/api/v1/admin/tours/${editingTour.id}?_method=PUT`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // Create new tour
          console.log('Creating new tour');
          response = await api.post('/api/v1/admin/tours', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        }
        
        console.log('API Response:', response.data);
        
        if (response.data.success) {
          toast.success(editingTour ? 'Tour updated successfully!' : 'Tour created successfully!');
          setShowForm(false);
          if (editingTour) setEditingTour(null);
          queryClient.invalidateQueries('tours');
        } else {
          toast.error(response.data.message || `Failed to ${editingTour ? 'update' : 'create'} tour. Please try again.`);
        }
      } catch (error) {
        console.error(`Error ${editingTour ? 'updating' : 'creating'} tour:`, error);
        
        if (error.response) {
          console.log('Error response:', error.response);
          
          if (error.response.status === 422) {
            const validationErrors = error.response.data.errors;
            if (validationErrors) {
              console.log('Validation errors:', validationErrors);
              const firstError = Object.values(validationErrors)[0];
              if (firstError && firstError[0]) {
                toast.error(firstError[0]);
              } else {
                toast.error('Validation failed. Please check your inputs and try again.');
              }
            } else {
              toast.error(error.response.data.message || `Failed to ${editingTour ? 'update' : 'create'} tour. Please try again.`);
            }
          } else {
            toast.error(`Failed to ${editingTour ? 'update' : 'create'} tour. Server returned: ${error.response.status}`);
          }
        } else {
          toast.error(`Failed to ${editingTour ? 'update' : 'create'} tour. Network error.`);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tours Management</h1>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => {
              console.log('Add Tour button clicked');
              setEditingTour(null);
              setShowForm(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center cursor-pointer"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Tour
          </button>
        </div>
      </div>

      {showForm ? (
        <TourForm
          tour={editingTour}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseForm}
          refetch={refetch}
          id={editingTour?.id}
        />
      ) : (
        <>
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tours..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button 
                onClick={() => handleSearch(searchQuery)}
                className="absolute right-2 top-2 p-1 bg-blue-500 text-white rounded"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : isError ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> Failed to load tours. Please try again later.</span>
            </div>
          ) : (
            <>
              <TourList
                tours={toursData?.data?.data || []}
                onEdit={handleEditTour}
                onDelete={handleDeleteTour}
                onToggleFeatured={handleToggleFeatured}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{toursData?.data?.data?.length || 0}</span> of <span className="font-medium">{toursData?.data?.meta?.total || 0}</span> results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">Page {currentPage} of {toursData?.data?.meta?.last_page || 1}</span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= (toursData?.data?.meta?.last_page || 1)}
                    className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ToursPage;
